// NOTE (2026-09-09, audit trail - stage 2 of 3, label lookup): the audit
// log stores raw field keys (e.g. "pharmacovigilanceTeamTrackedCaseNumber")
// because that's what's actually in the saved `data` object - it does not
// duplicate/store the human-readable label. This file builds a read-only
// lookup FROM the existing form field configs (CIOMS_FORM_FIELDS etc,
// which already pair every field's `name` with its `label`) so
// ChangeHistorySection can display the same label the form itself shows.
// It only READS those constants - it does not add, remove, or modify any
// field in them.
import { FORM_TYPES } from '@/interfaces';
import { FormField, FormFieldTypes } from '@Components/form/dynamic-form-fields';
import { CIOMS_FORM_FIELDS } from '@Components/form/constants/cioms';
import { PREGNANCY_FORM_FIELDS } from '@Components/form/constants/pregnancy';
import { SAE_AESI_FORM_FIELDS } from '@Components/form/constants/sae-aesi';
import { ADVERSE_EVENTS_FORM_FIELDS } from '@Components/form/constants/adverse-events';

const buildLabelMap = (fields: FormField[]): Record<string, string> => {
  const map: Record<string, string> = {};

  fields.forEach((field) => {
    if (field.type === FormFieldTypes.DESCRIPTION) {
      return;
    }
    if (field.type === FormFieldTypes.FORM_LIST) {
      (field.children || []).forEach((child) => {
        if (child.name) {
          map[String(child.name)] = child.label;
        }
      });
      return;
    }
    if (field.name) {
      map[String(field.name)] = field.label;
    }
  });

  return map;
};

const FIELD_LABELS_BY_FORM_TYPE: Partial<Record<FORM_TYPES, Record<string, string>>> = {
  [FORM_TYPES.CIOMS]: buildLabelMap(CIOMS_FORM_FIELDS),
  [FORM_TYPES.PREGNANCY]: buildLabelMap(PREGNANCY_FORM_FIELDS),
  [FORM_TYPES.SAE_AESI]: buildLabelMap(SAE_AESI_FORM_FIELDS),
  [FORM_TYPES.ADVERSE_EVENTS]: buildLabelMap(ADVERSE_EVENTS_FORM_FIELDS),
};

// Falls back to the raw field key (e.g. "locked", "saeNumber", or any
// field not found in the static form config) so a lookup miss never hides
// a change - it's just shown less prettily.
export const getFieldLabel = (formType: FORM_TYPES, fieldName: string): string => {
  const map = FIELD_LABELS_BY_FORM_TYPE[formType];
  return map?.[fieldName] || fieldName;
};
